import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import Lightbox from '../components/Lightbox';
import { LinksField, TagsField } from '../components/ListFields';
import { useAuth } from '../lib/auth';
import { deleteIdeaImage, rejectReason, uploadIdeaImage } from '../lib/images';
import { deleteIdea, updateIdea, useIdea } from '../lib/useIdeas';

export default function EditIdea() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  // Set by CreateIdea when the note saved but its images did not upload. The note is here to
  // try them again; the reason has to arrive with it, or the missing photos look like a bug.
  const notice = (useLocation().state as { notice?: string } | null)?.notice;
  const { idea, error: loadError, loading } = useIdea(user?.uid, id);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [links, setLinks] = useState<string[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Uploads write bytes immediately so the thumbnail can appear, but the URL only reaches
  // Firestore on save. Anything uploaded and then abandoned is tidied up on Cancel; the
  // server-side cleanupOrphanedImages sweep is the backstop for a closed tab.
  const uploadedUnsaved = useRef<string[]>([]);

  // Seed the form from the document once per note, not on every snapshot.
  //
  // `useIdea` is a live subscription, so a write from the phone apps mid-edit would otherwise
  // reset the fields under the cursor. Seeding once means the form is yours until you leave.
  const seeded = useRef<string | null>(null);
  useEffect(() => {
    if (!idea || seeded.current === idea.id) return;
    seeded.current = idea.id;
    setTitle(idea.title ?? '');
    setDescription(idea.description ?? '');
    setTags(idea.tags ?? []);
    setLinks(idea.links ?? []);
    setImages(idea.images ?? []);
  }, [idea]);

  // Every field is optional on read, so the comparison is against the same fallbacks the
  // form was seeded with. NUL join: a separator no tag or URL can contain.
  const dirty =
    !!idea &&
    (title !== (idea.title ?? '') ||
      description !== (idea.description ?? '') ||
      tags.join('\0') !== (idea.tags ?? []).join('\0') ||
      links.join('\0') !== (idea.links ?? []).join('\0') ||
      images.join('\0') !== (idea.images ?? []).join('\0'));

  async function addFiles(files: FileList | null) {
    if (!user || !id || !files?.length) return;
    setSaveError(null);

    // Checked here as well as in the rules, so an oversized file reads as a sentence rather
    // than as an opaque storage/unauthorized once the upload is already in flight.
    const rejected = Array.from(files).map(rejectReason).filter(Boolean);
    if (rejected.length) {
      setSaveError(rejected.join(' '));
      return;
    }

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const url = await uploadIdeaImage(user.uid, id, file);
        uploadedUnsaved.current.push(url);
        setImages((current) => [...current, url]);
      }
    } catch (e) {
      const code = (e as { code?: string }).code ?? '';
      setSaveError(`${(e as Error).message}${code ? ` (${code})` : ''}`);
    } finally {
      setUploading(false);
    }
  }

  /**
   * Remove objects nothing references any more, after the document no longer points at them.
   *
   * Never fails the caller: dropping the URL from the document is what matters, and an
   * object left behind is an orphan the server sweep collects rather than a broken image.
   */
  async function discardObjects(urls: string[]) {
    await Promise.allSettled(urls.map((url) => deleteIdeaImage(url)));
  }

  async function save() {
    if (!user || !id) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updateIdea(user.uid, id, { title, description, tags, links, images });

      // Only after the write lands. Deleting first would leave the document pointing at
      // bytes that no longer exist if the save then failed.
      const dropped = (idea?.images ?? []).filter((url) => !images.includes(url));
      uploadedUnsaved.current = [];
      void discardObjects(dropped);

      navigate('/');
    } catch (e) {
      // Visible, like every other failure path in this client. A silent catch here would
      // look exactly like a successful save that lost your edit.
      const code = (e as { code?: string }).code ?? '';
      setSaveError(`${(e as Error).message}${code ? ` (${code})` : ''}`);
      setSaving(false);
    }
  }

  async function remove() {
    if (!user || !id) return;
    setDeleting(true);
    setSaveError(null);
    try {
      await deleteIdea(user.uid, id, uploadedUnsaved.current);
      uploadedUnsaved.current = [];
      navigate('/');
    } catch (e) {
      const code = (e as { code?: string }).code ?? '';
      setSaveError(`${(e as Error).message}${code ? ` (${code})` : ''}`);
      setDeleting(false);
    }
  }

  const busy = saving || uploading || deleting;

  return (
    <>
      <AppHeader title="Edit note" />

      <main>
        {loading && <p className="muted">Loading…</p>}
        {loadError && <p className="error">{loadError}</p>}
        {notice && <p className="error">{notice}</p>}

        {!loading && !loadError && !idea && (
          <p className="muted">
            That note no longer exists. <Link to="/">Back to your notes</Link>.
          </p>
        )}

        {idea && (
          <form
            className="edit"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <label className="field">
              <span className="field-label">Title</span>
              <input
                type="text"
                value={title}
                placeholder="Untitled"
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>

            <label className="field">
              <span className="field-label">Description</span>
              <textarea
                rows={6}
                value={description}
                placeholder="What was the idea?"
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>

            <TagsField tags={tags} onChange={setTags} />
            <LinksField links={links} onChange={setLinks} />

            <div className="field">
              <span className="field-label">Images</span>
              {images.length > 0 && (
                <div className="thumbs">
                  {images.map((src, i) => (
                    <div className="thumb" key={src}>
                      {/* Captured on a phone, so orientation and aspect vary wildly;
                          object-fit keeps the grid even. The lightbox shows it uncropped. */}
                      <button
                        type="button"
                        className="thumb-open"
                        aria-label={`View image ${i + 1} of ${images.length}`}
                        onClick={() => setViewing(i)}
                      >
                        <img src={src} alt="" loading="lazy" />
                      </button>
                      <button
                        type="button"
                        className="remove"
                        aria-label="Remove image"
                        onClick={() => setImages(images.filter((i) => i !== src))}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className="add-image">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={uploading || saving}
                  onChange={(e) => {
                    void addFiles(e.target.files);
                    // Reset so picking the same file twice in a row still fires onChange.
                    e.target.value = '';
                  }}
                />
                <span>{uploading ? 'Uploading…' : 'Add images'}</span>
              </label>
              <p className="muted small hint">
                Removing an image here takes effect when you save.
              </p>
            </div>

            {saveError && <p className="error">{saveError}</p>}

            <div className="actions">
              <button className="primary" type="submit" disabled={busy || !dirty}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  void discardObjects(uploadedUnsaved.current);
                  uploadedUnsaved.current = [];
                  navigate('/');
                }}
              >
                Cancel
              </button>
            </div>

            <div className="delete-zone">
              {confirmingDelete ? (
                <div className="confirm" role="alert">
                  <p>
                    <strong>Delete this note?</strong> It disappears on every device, with
                    its images and any reminder. This cannot be undone.
                  </p>
                  <div className="actions">
                    <button type="button" className="danger" disabled={busy} onClick={() => void remove()}>
                      {deleting ? 'Deleting…' : 'Delete'}
                    </button>
                    <button type="button" disabled={deleting} onClick={() => setConfirmingDelete(false)}>
                      Keep it
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className="danger" disabled={busy} onClick={() => setConfirmingDelete(true)}>
                  Delete note
                </button>
              )}
            </div>
          </form>
        )}
      </main>

      {viewing !== null && (
        <Lightbox images={images} start={viewing} onClose={() => setViewing(null)} />
      )}
    </>
  );
}
